import BaseApiClient from '@api-client/BaseApiClient';
import axios from 'axios';

function returnbdxApiUrl(): string {
  const apiUrl =
    import.meta.env.VITE_IMPORT_URL ||
    'https://yk7lq4pfq2.execute-api.eu-west-1.amazonaws.com/development-db-refactor';

  try {
    new URL(apiUrl);
    return apiUrl;
  } catch (error) {
    console.error('Provided API URL is not valid:', apiUrl, error);
    throw new Error('Invalid API URL');
  }
}

export class ApiBDXImporter extends BaseApiClient {
  private bdxApiUrl = returnbdxApiUrl(); // Required: api gateway invoke url

  handleError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      throw error;
    } else {
      throw new Error('Something went wrong');
    }
  };

  async retrieveExistingMaps(companyUuid?: string | null) {
    if (!companyUuid) {
      throw new Error('Company uuid not found!');
    }
    try {
      const response = await this.client.get(`${this.bdxApiUrl}/mapper`, {
        headers: {
          'X-Company-Uuid': companyUuid,
          'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async retrieveFields(filePath: string, companyUuid?: string | null) {
    if (!companyUuid) {
      throw new Error('Company uuid not found!');
    }
    try {
      const response = await this.client.post(
        `${this.bdxApiUrl}/fields`,
        {
          url: filePath
        },
        {
          headers: {
            'X-Company-Uuid': companyUuid,
            'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
          }
        }
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async retrieveMappingSettings(mapId: string, companyUuid?: string | null) {
    if (!companyUuid) {
      throw new Error('Company uuid not found!');
    }
    try {
      const response = await this.client.get(
        `${this.bdxApiUrl}/mapper/${mapId}`,
        {
          headers: {
            'X-Company-Uuid': companyUuid,
            'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
          }
        }
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append('import', file);

    try {
      const response = await fetch('/api/policy/import', {
        method: 'POST',
        body: formData
      });

      return response.json();
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateMapping(
    mapId: string,
    data: { name: string; map: object; selectedProducts: string[] },
    companyUuid?: string | null
  ) {
    if (!companyUuid) {
      throw new Error('Company uuid not found!');
    }
    try {
      const response = await this.client.patch(
        `${this.bdxApiUrl}/mapper/${mapId}`,
        {
          name: data.name,
          map: data.map,
          selectedProducts: data.selectedProducts
        },
        {
          headers: {
            'X-Company-Uuid': companyUuid,
            'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
          }
        }
      );

      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createMapping(
    data: {
      name: string;
      map: object;
      selectedProducts: string[];
    },
    companyUuid?: string | null
  ) {
    try {
      if (!companyUuid) {
        throw new Error('Company uuid not found!');
      }
      const response = await this.client.post(
        `${this.bdxApiUrl}/mapper`,
        {
          name: data.name,
          map: data.map,
          selectedProducts: data.selectedProducts
        },
        {
          headers: {
            'X-Company-Uuid': companyUuid,
            'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
          }
        }
      );

      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async importData(
    mapId: string,
    filePath: string,
    declarationMonthYear: { month: string; year: string },
    user: { email: string; id: number },
    companyUuid?: string | null
  ) {
    if (!companyUuid) {
      throw new Error('Company uuid not found!');
    }

    const response = await this.client.post(
      `${this.bdxApiUrl}/validate/${mapId}`,
      {
        url: filePath,
        declarationMonthYear: `${declarationMonthYear.month}/${declarationMonthYear.year}`,
        user: user
      },
      {
        validateStatus: (status) => {
          return status <= 200;
        },
        headers: {
          'X-Company-Uuid': companyUuid,
          'api-key': import.meta.env.VITE_BDX_LAMBDA_API_KEY
        }
      }
    );

    return response;
  }
}

export { bdxImporterQueryKeys } from './query-keys';
export {
  useBdxImporterExistingMapsQuery,
  useBdxImporterFieldsQuery,
  useBdxImporterMappingSettingsQuery
} from './use-bdx-importer-queries';
export {
  useBdxImporterCreateMappingMutation,
  useBdxImporterImportDataMutation,
  useBdxImporterUpdateMappingMutation,
  useBdxImporterUploadFileMutation
} from './use-bdx-importer-mutations';
export type {
  BdxImporterCreateMappingVariables,
  BdxImporterImportDataVariables,
  BdxImporterUpdateMappingVariables
} from './use-bdx-importer-mutations';

